/**
 * CookieBanner — RODO-compliant cookie consent banner.
 *
 * 3 kategorie:
 *  • essential   — zawsze on (session, theme)
 *  • analytics   — opcjonalne (statystyki użytkowania)
 *  • marketing   — opcjonalne (kampanie)
 *
 * Zapisuje decyzję w localStorage + wysyła audit na /api/legal/consents.
 * Reagowanie na window event 'mrjerry:cookie-open' → otwiera ustawienia (z footera).
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Cookie, Check, X, Settings2, ShieldCheck } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const LS_KEY = 'mrjerry_cookie_consent_v1';

const DEFAULT_CATEGORIES = {
  essential: true,   // locked
  analytics: false,
  marketing: false,
};

const CookieBanner = () => {
  const [visible, setVisible] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [categories, setCategories] = useState(DEFAULT_CATEGORIES);

  // Show on first visit; also react to open event
  useEffect(() => {
    try {
      const stored = localStorage.getItem(LS_KEY);
      if (!stored) {
        setVisible(true);
      } else {
        const parsed = JSON.parse(stored);
        setCategories({ ...DEFAULT_CATEGORIES, ...parsed.categories });
      }
    } catch { setVisible(true); }

    const onOpen = () => { setVisible(true); setDetailsOpen(true); };
    window.addEventListener('mrjerry:cookie-open', onOpen);
    return () => window.removeEventListener('mrjerry:cookie-open', onOpen);
  }, []);

  const persist = (cats) => {
    const payload = {
      categories: cats,
      acceptedAt: new Date().toISOString(),
      version: 1,
    };
    try { localStorage.setItem(LS_KEY, JSON.stringify(payload)); } catch { /* ignore */ }
    // Fire event for other components
    window.dispatchEvent(new CustomEvent('mrjerry:cookie-changed', { detail: payload }));
    // Best-effort audit log
    axios.post(`${API}/api/legal/consents`, {
      context: 'cookie-banner',
      documentTypes: ['cookies'],
      cookieCategories: cats,
    }).catch(() => {});
  };

  const acceptAll = () => {
    const cats = { essential: true, analytics: true, marketing: true };
    setCategories(cats);
    persist(cats);
    setVisible(false);
    setDetailsOpen(false);
  };

  const rejectOptional = () => {
    const cats = { essential: true, analytics: false, marketing: false };
    setCategories(cats);
    persist(cats);
    setVisible(false);
    setDetailsOpen(false);
  };

  const saveCustom = () => {
    persist(categories);
    setVisible(false);
    setDetailsOpen(false);
  };

  if (!visible) return null;

  return (
    <>
      <div style={{
        position: 'fixed', bottom: 0, left: 0, right: 0,
        zIndex: 9500,
        background: 'rgba(6, 11, 22, 0.92)',
        backdropFilter: 'blur(16px)',
        borderTop: '1px solid rgba(212, 175, 55, 0.28)',
        boxShadow: '0 -20px 60px -20px rgba(0,0,0,0.5)',
        padding: '20px 20px 24px',
      }} data-testid="cookie-banner">
        <div style={{
          maxWidth: 1100, margin: '0 auto',
          display: 'grid', gap: 20,
          gridTemplateColumns: detailsOpen ? '1fr' : 'auto 1fr auto',
          alignItems: 'center',
        }} className="cb-inner">

          {!detailsOpen && (
            <>
              <div style={{
                width: 48, height: 48, borderRadius: 14,
                background: 'linear-gradient(135deg, rgba(212,175,55,0.22), rgba(184,134,11,0.08))',
                border: '1px solid rgba(212,175,55,0.3)',
                display: 'grid', placeItems: 'center', color: '#d4af37',
                flexShrink: 0,
              }} className="cb-icon">
                <Cookie size={22} />
              </div>

              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 800, color: '#f8fafc', marginBottom: 4, fontSize: 15 }}>
                  Używamy plików cookies
                </div>
                <div style={{ fontSize: 13, color: '#cbd5e1', lineHeight: 1.5 }}>
                  Stosujemy cookies niezbędne do działania aplikacji oraz, za Twoją zgodą, opcjonalne (analityczne / marketingowe). Szczegóły znajdziesz w{' '}
                  <Link to="/polityka-cookies" style={{ color: '#d4af37' }}>Polityce cookies</Link>.
                </div>
              </div>

              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button onClick={() => setDetailsOpen(true)}
                  style={btnGhost} data-testid="cookie-settings-btn">
                  <Settings2 size={14} /> Ustawienia
                </button>
                <button onClick={rejectOptional}
                  style={btnOutline} data-testid="cookie-reject-btn">
                  <X size={14} /> Odrzuć opcjonalne
                </button>
                <button onClick={acceptAll}
                  style={btnPrimary} data-testid="cookie-accept-all-btn">
                  <Check size={14} /> Akceptuj wszystkie
                </button>
              </div>
            </>
          )}

          {detailsOpen && (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                <div style={{
                  width: 40, height: 40, borderRadius: 12,
                  background: 'linear-gradient(135deg, rgba(212,175,55,0.22), rgba(184,134,11,0.08))',
                  border: '1px solid rgba(212,175,55,0.3)',
                  display: 'grid', placeItems: 'center', color: '#d4af37',
                }}>
                  <Cookie size={18} />
                </div>
                <div>
                  <div style={{ fontWeight: 800, color: '#f8fafc', fontSize: 15 }}>
                    Ustawienia plików cookies
                  </div>
                  <div style={{ fontSize: 12, color: '#94a3b8' }}>
                    Wybierz kategorie, na które wyrażasz zgodę
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <CategoryRow
                  id="essential"
                  title="Niezbędne (wymagane)"
                  desc="Umożliwiają logowanie, zapamiętanie preferencji UI. Bez nich aplikacja nie działa."
                  checked={true}
                  locked={true}
                />
                <CategoryRow
                  id="analytics"
                  title="Analityczne"
                  desc="Pomagają nam rozumieć, jak używana jest aplikacja i ją ulepszać (Google Analytics, wewnętrzna analityka)."
                  checked={categories.analytics}
                  onChange={(v) => setCategories((c) => ({ ...c, analytics: v }))}
                />
                <CategoryRow
                  id="marketing"
                  title="Marketingowe"
                  desc="Umożliwiają personalizację treści marketingowych i mierzenie skuteczności kampanii."
                  checked={categories.marketing}
                  onChange={(v) => setCategories((c) => ({ ...c, marketing: v }))}
                />
              </div>

              <div style={{ display: 'flex', gap: 8, marginTop: 18, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                <button onClick={rejectOptional} style={btnGhost} data-testid="cookie-reject-btn-2">Odrzuć opcjonalne</button>
                <button onClick={saveCustom} style={btnOutline} data-testid="cookie-save-btn">
                  <ShieldCheck size={14} /> Zapisz mój wybór
                </button>
                <button onClick={acceptAll} style={btnPrimary} data-testid="cookie-accept-all-btn-2">
                  <Check size={14} /> Akceptuj wszystkie
                </button>
              </div>
            </div>
          )}
        </div>

        <style>{`
          @media (max-width: 720px) {
            .cb-inner { grid-template-columns: 1fr !important; text-align: left; }
            .cb-icon { display: none !important; }
          }
        `}</style>
      </div>
    </>
  );
};

const CategoryRow = ({ id, title, desc, checked, onChange, locked = false }) => (
  <label style={{
    display: 'flex', alignItems: 'flex-start', gap: 12,
    padding: 14, borderRadius: 12,
    background: 'rgba(255,255,255,0.03)',
    border: `1px solid ${checked ? 'rgba(212,175,55,0.28)' : 'rgba(255,255,255,0.06)'}`,
    cursor: locked ? 'not-allowed' : 'pointer',
    opacity: locked ? 0.85 : 1,
  }} data-testid={`cookie-cat-${id}`}>
    <div
      onClick={() => !locked && onChange && onChange(!checked)}
      style={{
        flexShrink: 0, width: 42, height: 24, borderRadius: 999,
        background: checked ? 'linear-gradient(135deg,#d4af37,#b8860b)' : 'rgba(255,255,255,0.08)',
        border: '1px solid rgba(255,255,255,0.1)',
        position: 'relative', transition: 'background 0.2s ease',
        marginTop: 2,
      }}
    >
      <span style={{
        position: 'absolute', top: 2, left: checked ? 20 : 2,
        width: 18, height: 18, borderRadius: 999, background: '#fff',
        transition: 'left 0.2s ease', boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
      }} />
    </div>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontWeight: 700, color: '#f8fafc', fontSize: 13, marginBottom: 3 }}>
        {title}
        {locked && <span style={{ marginLeft: 8, fontSize: 10, color: '#d4af37', fontWeight: 800 }}>OBOWIĄZKOWE</span>}
      </div>
      <div style={{ fontSize: 12, color: '#94a3b8', lineHeight: 1.5 }}>{desc}</div>
    </div>
  </label>
);

const btnPrimary = {
  display: 'inline-flex', alignItems: 'center', gap: 6,
  padding: '10px 18px', borderRadius: 999,
  background: 'linear-gradient(135deg,#d4af37,#b8860b)',
  color: '#0b1220', border: 'none', cursor: 'pointer',
  fontWeight: 800, fontSize: 13,
  boxShadow: '0 10px 24px -10px rgba(212,175,55,0.5)',
};
const btnOutline = {
  display: 'inline-flex', alignItems: 'center', gap: 6,
  padding: '10px 18px', borderRadius: 999,
  background: 'transparent', color: '#d4af37',
  border: '1px solid #d4af37', cursor: 'pointer',
  fontWeight: 700, fontSize: 13,
};
const btnGhost = {
  display: 'inline-flex', alignItems: 'center', gap: 6,
  padding: '10px 16px', borderRadius: 999,
  background: 'transparent', color: '#cbd5e1',
  border: '1px solid rgba(255,255,255,0.1)', cursor: 'pointer',
  fontWeight: 600, fontSize: 12,
};

export default CookieBanner;
